// SCOPING A RUN TO A PLACE — "/initial-outreach illinois".
//
// The argument a person types is not a slug. They type "illinois", "IL", "il",
// "Illinois ", "chicago", "Chicago + NW Indiana", "californa" (typo) — and every
// one of those is a clear enough intention that refusing it would be the
// software being difficult. So the matcher is deliberately generous:
//
//   exact code      il            -> IL
//   state name      illinois      -> IL
//   region slug     illinois-chicago-nw-indiana -> IL
//   region label    chicago + nw indiana, il    -> IL
//   a city in it    chicago       -> IL
//   near miss       ilinois / californa         -> IL / CA  (edit distance 1-2)
//
// and it is generous in ONE direction only: it never guesses between two states
// that match equally well. An ambiguous input resolves to nothing and the caller
// offers the candidates, because running the wrong state is far worse than
// asking.
//
// The option list is derived from the manifest — the regions the directory
// actually holds — so a state with no leads is never offered, and adding a
// region makes it typeable without touching this file.

// The 50 states + DC, by postal code. Only used to turn a slug or a typed name
// into a code; a state absent from the manifest never becomes an option.
const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
}
const NAME_TO_CODE = Object.fromEntries(
  Object.entries(STATE_NAMES).map(([code, name]) => [name.toLowerCase(), code]),
)

const norm = (v) => String(v ?? '').trim().toLowerCase()
const squash = (v) => norm(v).replace(/[^a-z0-9]+/g, '')

/**
 * Does any WORD in `text` start with `q`? "corpus" matches "South TX (Corpus &
 * Brownsville)"; "il" does not, even though the letters appear inside
 * "Brownsville". This is the difference between an autocomplete you can trust
 * and a list of coincidences.
 */
function startsAWord(text, q) {
  const needle = norm(q)
  if (!needle) return false
  return norm(text)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .some((word) => word.startsWith(needle))
}

/**
 * The state a region belongs to, from its slug then its label.
 *
 * Slugs lead with the state ("illinois-chicago-nw-indiana", "texas-gulf-houston")
 * and labels trail with the code ("Chicago + NW Indiana, IL"). A region that
 * says neither returns '' and is simply not offered under any state, rather
 * than being filed under a guess.
 */
export function regionState(region) {
  const slug = norm(region?.slug)
  const head = slug.split('-')[0]
  if (NAME_TO_CODE[head]) return NAME_TO_CODE[head]
  // Two-word state names lead with two slug segments ("new-york-…").
  const head2 = slug.split('-').slice(0, 2).join(' ')
  if (NAME_TO_CODE[head2]) return NAME_TO_CODE[head2]
  const label = String(region?.label || '')
  const trailing = label.match(/\b([A-Z]{2})\b\s*$/) || label.match(/\(([A-Z]{2})[-)]/)
  if (trailing && STATE_NAMES[trailing[1]]) return trailing[1]
  return ''
}

/**
 * Every state the directory actually has regions for, with its regions and
 * lead count. Sorted by size, because that is the order a person scanning a
 * list of places expects.
 *
 * @returns {{code, name, regions: string[], labels: string[], count: number}[]}
 */
export function statesFromManifest(manifest, category) {
  const regions = manifest?.categories?.[category] || []
  const by = new Map()
  for (const region of regions) {
    const code = regionState(region)
    if (!code) continue
    const entry = by.get(code) || { code, name: STATE_NAMES[code], regions: [], labels: [], count: 0 }
    entry.regions.push(region.slug)
    entry.labels.push(region.label || region.slug)
    entry.count += Number(region.count) || 0
    by.set(code, entry)
  }
  return [...by.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

// Levenshtein, bounded — only ever used to offer a near miss, never to pick one
// silently over an exact match.
function distance(a, b) {
  if (a === b) return 0
  if (!a.length || !b.length) return Math.max(a.length, b.length)
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i]
    for (let j = 1; j <= b.length; j += 1) {
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    }
    prev = row
  }
  return prev[b.length]
}

/**
 * Rank the states against what has been typed so far — the autocomplete list.
 * An empty query lists everything (the menu you get from a bare space).
 *
 * Ordering is by how the match was made, strongest first, so the top row is
 * always the one a person meant: the code they typed, then the state name they
 * started, then a region inside it, then a typo.
 */
export function matchStates(query, states) {
  const q = norm(query)
  const sq = squash(query)
  if (!q) return states.map((s) => ({ ...s, why: '' }))
  const scored = []
  for (const s of states) {
    const code = s.code.toLowerCase()
    const name = s.name.toLowerCase()
    const sname = squash(s.name)
    let rank = null
    let why = ''
    if (code === q) {
      rank = 0
    } else if (name === q || sname === sq) {
      rank = 1
    } else if (sname.startsWith(sq)) {
      rank = 2
    } else if (s.regions.some((r) => squash(r) === sq) || s.labels.some((l) => squash(l) === sq)) {
      rank = 3
      why = 'region'
    } else if (q.length >= 3 && s.labels.some((l) => startsAWord(l, q))) {
      // "chicago" -> Illinois. Naming a place inside the state is a normal way
      // to say the state, so it ranks above a typo but below the state itself.
      //
      // WORD STARTS ONLY, and never for one or two letters. Matching a bare
      // substring anywhere in a label made "il" match South TX (Corpus &
      // Brownsvi-ll-e) and "ca" match Chi-ca-go — a list where two of the three
      // rows are noise teaches people not to trust the list.
      rank = 4
      why = s.labels.find((l) => startsAWord(l, q)) || 'region'
    } else if (sq.length >= 4 && distance(sname, sq) <= 2) {
      // A typo in a state NAME is recoverable ("ilinois"). A typo in a two
      // letter CODE is not: every code is one edit from several others, so
      // "il" would offer FL. Codes must be typed correctly; they are two keys.
      rank = 5
      why = 'did you mean'
    }
    if (rank !== null) scored.push({ ...s, rank, why })
  }
  scored.sort((a, b) => a.rank - b.rank || b.count - a.count)
  return scored
}

/**
 * Resolve typed text to exactly one state, or explain why not.
 *
 * `state` is set ONLY when the answer is unambiguous: a single match, or a
 * clear winner (its rank strictly better than the runner-up). Two equally good
 * matches resolve to nothing with both offered — picking one would be a coin
 * flip against the user's book of business.
 *
 * @returns {{state: object|null, matches: object[], reason: string}}
 */
export function resolveState(query, states) {
  const q = norm(query)
  if (!q) return { state: null, matches: states, reason: '' }
  const matches = matchStates(q, states)
  if (!matches.length) {
    return { state: null, matches: [], reason: `No leads in “${String(query).trim()}”.` }
  }
  if (matches.length === 1) return { state: matches[0], matches, reason: '' }
  if (matches[0].rank < matches[1].rank) return { state: matches[0], matches, reason: '' }
  return { state: null, matches, reason: `“${String(query).trim()}” matches more than one place.` }
}

/**
 * Does this lead sit in `code`? The row's own city_state is the authority —
 * it is the field the import wrote per company — with the region it was loaded
 * from as the fallback for a row that carries no city.
 *
 * A row with NEITHER (a CRM-only company, which has no directory geography)
 * answers false: it is not evidence of being in the state, and a scoped run
 * that quietly included unplaceable rows would not be scoped at all.
 */
export function rowInState(row, code) {
  if (!code) return true
  const cs = String(row?.city_state || row?.cityState || '')
  const m = cs.match(/,\s*([A-Za-z]{2})\b/)
  if (m) return m[1].toUpperCase() === code
  const region = norm(row?._region || row?.region)
  if (region) {
    const head = region.split('-')[0]
    if (NAME_TO_CODE[head]) return NAME_TO_CODE[head] === code
    const head2 = region.split('-').slice(0, 2).join(' ')
    if (NAME_TO_CODE[head2]) return NAME_TO_CODE[head2] === code
  }
  return false
}

export { STATE_NAMES }
